import { IFluidObject } from '@fluidframework/core-interfaces';
import { ComponentProduceCommands, CommandProps, CommandsUpdate } from '@fluidx/office-fluid-types';
import { PostMessageSender, PostMessageHandlers } from '../Shared/PostMessage';
import {
  GetCommandsMessage,
  GetCommandsReturnedMessage,
  ExecuteCommandMessage,
  CommandStateUpdateMessage,
  ReturnedSerializableCommand
} from '../MessageTypes/ProduceCommandsMessageTypes';
import { EventEmitter } from 'events';

/**
 * Initializes everything needed to handle the ProduceCommands interface outside the
 * iframe.
 * @param sender how to send messages to the inside of the iframe
 * @returns A dummy component with just this interface and the set of message
 * handlers this interface is expecting
 */
export function getHostSideProduceCommands(
  sender: PostMessageSender
): {
  component: ComponentProduceCommands;
  handlers: PostMessageHandlers<string>;
  dispose: () => void;
} {
  const component = new HostSideProduceCommandProxy(sender);

  const handlers: PostMessageHandlers<string> = {
    ['ComponentProduceCommands_CommandStateUpdate']: (message) => {
      const updates: CommandsUpdate = (message as CommandStateUpdateMessage).payload;
      component.emit('commandsUpdated', updates);
    }
  };
  return {
    component,
    handlers,
    dispose: () => component.removeAllListeners()
  };
}

class HostSideProduceCommandProxy extends EventEmitter implements ComponentProduceCommands {
  constructor(private sender: PostMessageSender) {
    super();
  }

  get ComponentProduceCommands(): ComponentProduceCommands {
    return this;
  }

  async getCommands(): Promise<CommandProps[]> {
    const message: GetCommandsMessage = {
      type: 'ComponentProduceCommands_GetCommands'
    };
    const reply = await this.sender.sendWithReturn(message);
    const payload = (reply as GetCommandsReturnedMessage).payload;
    return this.getHostSideCommands(payload);
  }

  public dispose() {
    this.removeAllListeners();
  }

  // Parse the returned commands into CommandProps by adding execute function that sends message across
  // the iframe with the index of the command to be executed.
  private getHostSideCommands(returnedCommands: ReturnedSerializableCommand[]): CommandProps[] {
    return returnedCommands.map((command) => {
      const { executeId, ...commandingSurfaceCommandProps } = command;
      const executeCommand = () => {
        const message: ExecuteCommandMessage = {
          type: 'ComponentProduceCommands_Execute',
          payload: { commandId: executeId }
        };
        this.sender.send(message);
      };
      return { ...commandingSurfaceCommandProps, execute: executeCommand };
    });
  }
}

/**
 * Initializes everything needed to handle the ProduceCommands interface inside the
 * iframe.
 * @param component the actual component with the real implementation of the interface
 * @param sender how to send messages to the outside of the iframe
 */
export function getGuestSideProduceCommands(
  component: IFluidObject,
  sender: PostMessageSender
): PostMessageHandlers<string> | undefined {
  const componentProduceCommands = component.ComponentProduceCommands;

  if (!componentProduceCommands) {
    return undefined;
  }

  component.ComponentProduceCommands?.on('commandsUpdated', (commandUpdates) => {
    // Send a message to the host to notify that the commandUpdated event has been emitted
    const updateMessage: CommandStateUpdateMessage = {
      type: 'ComponentProduceCommands_CommandStateUpdate',
      payload: commandUpdates
    };
    sender.send(updateMessage);
  });

  let executeCommandList: { [id: number]: Function };
  let nextCommandId = 0;
  executeCommandList = {};

  return {
    ['ComponentProduceCommands_GetCommands']: async (message) => {
      const commands = await componentProduceCommands.getCommands();

      let serializableCommandList: ReturnedSerializableCommand[] = [];

      // Remove execute command from the CommandingSurfaceCommand to make it serializable, store the execute functions in an dictionary
      // with an id. Execute will be replaced by a function on the host side that sends a message with the command id to be executed.
      commands.forEach((command) => {
        const { execute, ...otherProps } = command;
        executeCommandList[nextCommandId] = execute
          ? execute
          : () => {
              /* Do Nothing */
            };
        serializableCommandList.push({ ...otherProps, executeId: nextCommandId });
        nextCommandId = nextCommandId + 1;
      });

      const reply: GetCommandsReturnedMessage = {
        payload: serializableCommandList
      };
      sender.sendReply(message, reply);
    },

    ['ComponentProduceCommands_Execute']: (message) => {
      const { commandId } = (message as ExecuteCommandMessage).payload;
      const commandToExecute = executeCommandList[commandId];
      commandToExecute();
    }
  };
}
